/**
 * IMPORTANT!!!
 * DO NOT EDIT THIS FILE UNLESS YOU KNOW EXACTLY WHAT YOU'RE DOING.
 */
import { ReactNode } from 'react';
import {
  BehaviorSubject,
  distinctUntilChanged,
  first,
  map,
  Observable,
  Subject,
  Subscription,
  switchMap,
} from 'rxjs';

const modalHelper = <
  T1 extends {
    [key in string]: object;
  },
>() => {
  const bs = new BehaviorSubject<{
    type: keyof T1;
    data: T1[keyof T1];
  } | null>(null);
  return {
    openModal: <T extends keyof T1>(type: T, data: T1[T]) =>
      bs.next({
        type,
        data,
      }),
    $getModal: <T extends keyof T1>(type: T) =>
      bs.pipe(
        map((m) => (m?.type === type ? (m?.data as T1[T]) : null)),
        distinctUntilChanged()
      ) as Observable<T1[T] | null>,
    closeModal: () => bs.next(null),
  };
};

/**
 * Constructor does not seem to guarantee run once when it comes to http requests
 * This is same as Angular's ngOnInit
 * Runs once when the BLoC is created
 * Use when you want to run logic on init. Never put any logic into ctor
 */
export interface IBLoCInitialisable {
  onInit?: () => void;
}

/**
 * In case there are any things that BLoC needs to clean up add this
 * interface to your bloc and you will be able to add logic
 * that you can run when BLoC gets destroyed
 */
export interface IBLoCDestroyable {
  onDestroy?: () => void;
}

/**
 * Type helper for BLoC wrapper component
 *
 * @param children  You can pass a ReactNode (as usual) or if you need you can pass in
 *                  a function that would pass in an instance of BLoC.
 *
 * @param Partial<State> Partial state that you may want to pass from main smart component
 */
export type BLoCParams<B extends BLoCBase<State>, State extends object, T = {}> = T & {
  children?: ReactNode | ((bloc: B) => ReactNode | null) | undefined;
} & Partial<State>;

export const renderBlocChild = <B extends Readonly<BLoCBase<object>>>(
  children: ReactNode | ((bloc: B) => ReactNode | null) | undefined,
  bloc: B
): ReactNode => {
  if (typeof children === 'function') return children(bloc);
  return children;
};

/**
 * Summary.       Your BLoC components should extend from this class.
 *
 * Description.   This class stores current BLoC state in a Subject.
 *                Class contains mutators and getter helper functions for dealing with state.
 *                Instance of this class should always be frozen to avoid introducing any mutable state
 *
 * @argument initState  Pass in any initial state that you want this BLoC to initialise with.
 */
export class BLoCBase<State extends object> {
  private readonly state: BehaviorSubject<State>;
  private subs: { [key in string]: Subscription } = {};

  constructor(initState: State | null = null) {
    this.state = new BehaviorSubject<State>(initState ?? ({} as State));
  }

  /**
   * Adds subscription to the list of cleanups
   * Use when you need to directly sub to Observable from BLoC
   * @param sub Subscription object
   * @param key Key that you can use to cancel manual
   */
  protected addSub = (sub: Subscription, key?: string) => {
    key && this.unsub(key);
    this.subs[key ?? Object.keys(this.subs).length] = sub;
  };

  /**
   * Use to manually unsub Sub that was added via addSub method
   * @param key Unique key that was passed using addSub
   */
  public unsub = (key?: string) => {
    if (!key) {
      Object.values(this.subs).forEach((s) => s.unsubscribe());
    } else if (key && this.subs[key]) {
      this.subs[key].unsubscribe();
    }
  };

  /**
   * Returns current state as of now.
   * Use as a quick hand to get current state within BLoC
   * This is done to simplify. Do not abuse.
   * Use $getState instead if you can.
   * @param name State var name
   * @returns Frozen Readonly version of current state for given name
   */
  protected readonly currentState = <K extends keyof State>(name: K) => this.state.value[name];

  /**
   *
   * @param name Name of the state key that you would like to access
   * @returns An Observable that provide access to the key. This will only emit on distinct change.
   *          So when state is set if this key was not changed it will not emit.
   *          Note that if data type is not object (num,str,etc) observable will not re-emit until value has changed
   *          and object will emit if ref has changed
   */
  protected readonly $getState = <K extends keyof State>(name: K) =>
    this.state.pipe(
      map((a) => a[name]),
      distinctUntilChanged()
    );

  /**
   *
   * @param mapper  a function that takes state and then allows you to create an observable from
   *                a specific path
   * @returns Same as $getState
   */
  protected readonly $mapState = <R,>(mapper: (s: State) => R): Observable<R> =>
    this.state.pipe(map(mapper), distinctUntilChanged());

  /**
   *
   * @param name Key of the state to change
   * @param value New value, Must be different from prev value or it will not set. If object will freeze
   * @returns void
   */
  protected readonly setState = <K extends keyof State, Z extends State[K]>(name: K, value: Z): void => {
    this.state.value[name] !== value &&
      this.state.next(
        Object.assign({}, this.state.value, {
          [name]: value && typeof value === 'object' ? Object.freeze(value) : value, // ensure state is immutable
        })
      );
  };

  /**
   *
   * @param name Key of the state to change
   * @param mutator Takes in a function that passes previous state and expects new state to be returned
   * @returns void
   */
  protected readonly mutateState = <K extends keyof State>(
    name: K,
    mutator: (prev: State[K]) => State[K]
  ): void => {
    const prev = this.currentState(name);
    this.state.next(
      Object.assign({}, this.state.value, {
        [name]: mutator(prev),
      })
    );
  };

  /**
   *
   * @param partialState Pass in the whole new state object (partial) and it will override any matching state
   * @returns void
   */
  protected readonly setStates = (partialState: Partial<State>): void =>
    this.state.next(Object.assign({}, this.state.value, partialState));

  protected readonly firstAndSwitchMap = <T,>($ob: Observable<T>, res: (r: T) => Observable<any>) =>
    $ob.pipe(first(), switchMap(res));
}

export class BLoCWithModalsBase<
  State extends object,
  TModals extends {
    [key in string]: object;
  } = {},
> extends BLoCBase<State> {
  private readonly modalState = modalHelper<TModals>();
  public $getModal = this.modalState.$getModal;
  protected _openModal = this.modalState.openModal;
  protected _closeModal = this.modalState.closeModal;
}

/**
 * Create a new subject based on a pre-defined type.
 * Exposes two methods:
 * - subscribe: to subscribe passing an object with observers.
 * - notify: to fire an event name based on the pre-defined events.
 */
export const createSubjectableBLoC = <T extends { [key: string]: Record<string, any> | null }>() => {
  const $subject = new Subject<{ type: keyof T; payload: T[keyof T] }>();
  return {
    subscribe: (observers: Partial<{ [K in keyof T]: (payload: T[K]) => void }>) =>
      $subject.subscribe((ev) => {
        observers[ev.type]?.(ev.payload);
      }),
    notify: <E extends keyof T>(event: E, payload: T[E]) => {
      $subject.next({ type: event, payload });
    },
    $subject,
  };
};
